import { Component, ElementRef, computed, inject, input, signal, viewChild } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { tablerLock, tablerX } from '@ng-icons/tabler-icons';
import { UserService } from '../../services/user-service/user.service';
import { AssetService } from '../../services/asset-service/asset.service';
import { Loader } from '../loader';
import { PrimaryBtnDirective } from '../primary-button';
import { environment } from '../../../environments/environment';

interface RequestData {
  justification: string;
  reason: string;
  project_name: string;
}

@Component({
  selector: 'request-access',
  imports: [NgIcon, Loader, PrimaryBtnDirective],
  providers: [provideIcons({ tablerX, tablerLock })],
  templateUrl: './request-access.html',
  styleUrl: './request-access.scss',
})
export class RequestAccess {
  private dialog = viewChild.required<ElementRef<HTMLDialogElement>>('dialog');

  readonly assetName = input.required<string>();
  readonly isIconButton = input<boolean>();
  readonly assetId = input.required<string>();

  // not actually used, was originall for mailto
  readonly ownerName = input.required<string | null>();
  readonly ownerEmail = input.required<string | null>();

  private userService = inject(UserService);
  private sanitizer = inject(DomSanitizer);
  private assetService = inject(AssetService);

  private userDetails = this.userService.getUserDetails();
  private userPhoto = this.userService.getUserPhoto();

  protected selectedReason = signal<string | null>(null);
  protected justification = signal('');
  protected projectName = signal('');
  isPending = signal(false);

  protected readonly reasons = [
    { id: 'exploring', label: "I'm just exploring" },
    { id: 'project', label: 'I need this asset for a project' },
  ];

  protected user = computed(() => this.userDetails.data());

  protected userPhotoUrl = computed(() => {
    if (!this.userPhoto.isSuccess()) return undefined;
    const blob = this.userPhoto.data();
    return blob ? this.sanitizer.bypassSecurityTrustUrl(URL.createObjectURL(blob)) : undefined;
  });

  protected userInitials = computed(() =>
    (this.user()?.displayName ?? '')
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((w) => w[0] ?? '')
      .join('')
      .toUpperCase(),
  );

  protected userMeta = computed(() => {
    const u = this.user();
    if (!u) return '';
    const parts = [u.userPrincipalName, u.department].filter(Boolean);
    return parts.join(' · ');
  });

  protected canSubmit = computed(
    () => !!this.selectedReason() && this.justification().trim().length > 0,
  );

  protected open(e: Event): void {
    e.stopPropagation();
    this.userDetails.refetch();
    this.userPhoto.refetch();
    this.dialog().nativeElement.showModal();
  }

  protected close(): void {
    this.dialog().nativeElement.close();
    this.selectedReason.set(null);
    this.justification.set('');
    this.projectName.set('');
  }

  protected onDialogClick(e: MouseEvent): void {
    e.stopPropagation();
    if ((e.target as HTMLElement) === this.dialog().nativeElement) this.close();
  }

  protected submit(): void {
    if (!this.canSubmit()) return;

    this.isPending.set(true);

    const requestData: RequestData = {
      justification: this.justification(),
      reason: this.selectedReason() ?? '', // canSubmit should already check if this has a value
      project_name: this.projectName(),
    };

    this.assetService.requestAccess(this.assetId, requestData).subscribe({
      next: () => {
        this.close();
        this.generateRequestAccessMailto(requestData);
      },
      complete: () => {
        this.isPending.set(false);
      },
    });
  }

  generateRequestAccessMailto = ({ justification, reason, project_name }: RequestData) => {
    const link = `mailto:${environment.DATANEX_SUPPORT_EMAIL}?subject=Requesting%20for%20access%20to%20${encodeURIComponent(this.assetName())}&body=Requesting%20access%20to%20the%20following%20asset%20${this.assetName()}%0A%0ARequest%20Reason%20%3A%20${encodeURIComponent(reason)}%0A%0AJustification%20%3A%20${encodeURIComponent(justification)}%0A%0AProject%20Name%20%3A%20${encodeURIComponent(project_name)}`;

    window.location.href = link;
  };
}
